// src/components/admin/AdminRewards.js
import React, { useState, useEffect } from 'react';
import { storage } from '../../services/firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db } from '../../services/firebase/config';
import {
  collection, getDocs, doc, setDoc, deleteDoc, updateDoc, serverTimestamp, getDoc
} from 'firebase/firestore';
import RewardAnimation from '../rewards/RewardAnimation';

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) return 'File must be under 2MB';
  return null;
};

const ANIMATION_TYPES = [
  { id: 'fireworks', label: 'Fireworks', emoji: '🎆' },
  { id: 'confetti', label: 'Confetti', emoji: '🎊' },
];

export default function AdminRewards() {
  const [tab, setTab] = useState('animations');
  const [statusMsg, setStatusMsg] = useState(null);

  const showMsg = (text, type = 'success') => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  const TABS = [
    { id: 'animations', label: 'Animations' },
    { id: 'village', label: 'Village Shoutout' },
    { id: 'streak', label: 'Streak Celebration' },
    { id: 'memes', label: 'Media Rewards' },
  ];

  return (
    <div>
      <div className="admin-section-title">Rewards</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id}
            className={`admin-btn ${tab === t.id ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      {statusMsg && (
        <div className={`admin-status-msg ${statusMsg.type}`} style={{ marginBottom: '16px' }}>
          {statusMsg.text}
        </div>
      )}
      {tab === 'animations' && <AnimationsTab showMsg={showMsg} />}
      {tab === 'village' && <VillageShoutoutTab showMsg={showMsg} />}
      {tab === 'streak' && <StreakCelebrationTab showMsg={showMsg} />}
      {tab === 'memes' && <MediaRewardsTab showMsg={showMsg} />}
    </div>
  );
}

// ===== ANIMATIONS =====
function AnimationsTab({ showMsg }) {
  const [animations, setAnimations] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewAnim, setPreviewAnim] = useState(null);
  const [audioFiles, setAudioFiles] = useState({});
  const [audioUploading, setAudioUploading] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const existing = {};
      const snap = await getDocs(collection(db, 'rewardContent'));
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.type === 'animation' && ANIMATION_TYPES.find(a => a.id === data.animationId)) {
          existing[data.animationId] = { id: d.id, ...data };
        }
      });
      for (const anim of ANIMATION_TYPES) {
        if (!existing[anim.id]) {
          await setDoc(doc(db, 'rewardContent', `animation_${anim.id}`), {
            type: 'animation', animationId: anim.id, active: true,
            duration: 5, createdAt: serverTimestamp()
          });
          existing[anim.id] = { animationId: anim.id, active: true, duration: 5 };
        }
      }
      setAnimations(existing);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (anim) => {
    const current = animations[anim.id];
    const newActive = !current?.active;
    if (!window.confirm(`${newActive ? 'Activate' : 'Deactivate'} ${anim.label}?`)) return;
    try {
      await setDoc(doc(db, 'rewardContent', `animation_${anim.id}`), {
        type: 'animation', animationId: anim.id,
        active: newActive,
        duration: current?.duration || 5,
        audioURL: current?.audioURL || '',
        updatedAt: serverTimestamp()
      });
      showMsg(`${anim.label} ${newActive ? 'activated' : 'deactivated'}`);
      load();
    } catch (e) {
      showMsg('Error updating', 'error');
    }
  };

  const handleDurationChange = async (anim, newDuration) => {
    const val = Math.min(10, Math.max(3, parseInt(newDuration) || 5));
    const current = animations[anim.id];
    try {
      await setDoc(doc(db, 'rewardContent', `animation_${anim.id}`), {
        type: 'animation', animationId: anim.id,
        active: current?.active !== false,
        duration: val,
        audioURL: current?.audioURL || '',
        updatedAt: serverTimestamp()
      });
      setAnimations(prev => ({ ...prev, [anim.id]: { ...prev[anim.id], duration: val } }));
      showMsg(`${anim.label} duration updated to ${val}s`);
    } catch (e) {
      showMsg('Error updating duration', 'error');
    }
  };

  const handleAudioChange = (animId, file) => {
    if (!file) return;
    const err = validateFile(file);
    if (err) { showMsg(err, 'error'); return; }
    setAudioFiles(prev => ({ ...prev, [animId]: file }));
  };

  const handleAudioUpload = async (anim) => {
    const file = audioFiles[anim.id];
    if (!file) { showMsg('Please select an audio file first', 'error'); return; }
    if (!window.confirm(`Upload audio for ${anim.label}?`)) return;
    setAudioUploading(anim.id);
    try {
      const current = animations[anim.id];
      const audRef = ref(storage, `content/animation-audio/${anim.id}_${Date.now()}`);
      await uploadBytes(audRef, file);
      const audioURL = await getDownloadURL(audRef);
      await setDoc(doc(db, 'rewardContent', `animation_${anim.id}`), {
        type: 'animation', animationId: anim.id,
        active: current?.active !== false,
        duration: current?.duration || 5,
        audioURL,
        updatedAt: serverTimestamp()
      });
      showMsg(`Audio uploaded for ${anim.label}`);
      setAudioFiles(prev => ({ ...prev, [anim.id]: null }));
      load();
    } catch (e) {
      showMsg('Error uploading audio', 'error');
    } finally {
      setAudioUploading(null);
    }
  };

  const handleAudioRemove = async (anim) => {
    if (!window.confirm(`Remove audio for ${anim.label}?`)) return;
    const current = animations[anim.id];
    try {
      if (current?.audioURL) {
        const audRef = ref(storage, `content/animation-audio/${anim.id}`);
        await deleteObject(audRef).catch(() => {});
      }
      await setDoc(doc(db, 'rewardContent', `animation_${anim.id}`), {
        type: 'animation', animationId: anim.id,
        active: current?.active !== false,
        duration: current?.duration || 5,
        audioURL: '',
        updatedAt: serverTimestamp()
      });
      showMsg(`Audio removed for ${anim.label}`);
      load();
    } catch (e) {
      showMsg('Error removing audio', 'error');
    }
  };

  return (
    <div>
      {previewAnim && (
        <RewardAnimation
          type={previewAnim.id}
          duration={animations[previewAnim.id]?.duration || 5}
          name="Rohan"
          audioURL={animations[previewAnim.id]?.audioURL || null}
          language="mr"
          isPreview={true}
          onComplete={() => setPreviewAnim(null)}
        />
      )}
      <div className="admin-card">
        <h3>Celebration Animations</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>
          Toggle active/inactive, set duration, and upload audio for each animation.
        </p>
        {loading ? <p>लोड होत आहे...</p> : ANIMATION_TYPES.map(anim => {
          const current = animations[anim.id];
          const isActive = current?.active !== false;
          const duration = current?.duration || 5;
          const hasAudio = !!current?.audioURL;
          return (
            <div key={anim.id} style={{
              padding: '16px', marginBottom: '12px',
              background: isActive ? '#f0f8ff' : '#fef2f2',
              borderRadius: '10px',
              border: `1.5px solid ${isActive ? '#007fff33' : '#dc354533'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <span style={{ fontSize: '28px', flexShrink: 0 }}>{anim.emoji}</span>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <div style={{ fontWeight: 600, fontSize: '15px' }}>{anim.label}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#888' }}>Duration:</span>
                  <input type="number" min="3" max="10" value={duration}
                    onChange={e => handleDurationChange(anim, e.target.value)}
                    style={{ width: '52px', padding: '4px 8px', border: '1px solid #ddd',
                      borderRadius: '6px', fontSize: '14px', textAlign: 'center' }} />
                  <span style={{ fontSize: '13px', color: '#888' }}>sec</span>
                </div>
                <span style={{ fontSize: '12px', fontWeight: 600,
                  color: isActive ? '#28a745' : '#dc3545', minWidth: '55px' }}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
                <button className="admin-btn admin-btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '13px' }}
                  onClick={() => setPreviewAnim(anim)}>
                  Preview
                </button>
                <button
                  className={`admin-btn ${isActive ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
                  style={{ padding: '6px 14px', fontSize: '13px' }}
                  onClick={() => handleToggle(anim)}>
                  {isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
              <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#555' }}>
                  Audio {hasAudio ? '✓ Uploaded' : '(none — plays silently)'}
                </div>
                {hasAudio && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px',
                    background: '#e8f5e9', padding: '8px 12px', borderRadius: '8px' }}>
                    <audio controls src={current.audioURL} style={{ height: '32px', flex: 1 }} />
                    <button className="admin-btn admin-btn-danger"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                      onClick={() => handleAudioRemove(anim)}>
                      Remove
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <input type="file" accept="audio/*"
                    onChange={e => handleAudioChange(anim.id, e.target.files[0])}
                    style={{ fontSize: '13px' }} />
                  {audioFiles[anim.id] && (
                    <button className="admin-btn admin-btn-primary"
                      style={{ padding: '6px 14px', fontSize: '13px' }}
                      onClick={() => handleAudioUpload(anim)}
                      disabled={audioUploading === anim.id}>
                      {audioUploading === anim.id ? 'Uploading...' : hasAudio ? 'Replace Audio' : 'Upload Audio'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== VILLAGE SHOUTOUT =====
function VillageShoutoutTab({ showMsg }) {
  const [message, setMessage] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [existingAudioURL, setExistingAudioURL] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'rewardContent', 'village_shoutout'));
      if (snap.exists()) {
        setMessage(snap.data().message || '');
        setActive(snap.data().active !== false);
        setExistingAudioURL(snap.data().audioURL || '');
      } else {
        setMessage('[Name], आज [Village] ने [Points] गुण मिळवले! तू त्यात होतास 💪');
        setActive(true);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!message.trim()) { showMsg('Message cannot be empty', 'error'); return; }
    if (!window.confirm('Save village shoutout?')) return;
    setSaving(true);
    try {
      let audioURL = existingAudioURL;
      if (audioFile) {
        setAudioUploading(true);
        const audRef = ref(storage, `content/reward-audio/village_shoutout_${Date.now()}`);
        await uploadBytes(audRef, audioFile);
        audioURL = await getDownloadURL(audRef);
        setAudioUploading(false);
      }
      await setDoc(doc(db, 'rewardContent', 'village_shoutout'), {
        type: 'village_shoutout', message: message,
        active, audioURL, updatedAt: serverTimestamp()
      });
      showMsg('Village shoutout saved');
      setAudioFile(null);
      load();
    } catch (e) {
      showMsg('Error saving', 'error');
    } finally {
      setSaving(false);
      setAudioUploading(false);
    }
  };

  const handleAudioRemove = async () => {
    if (!window.confirm('Remove audio?')) return;
    try {
      await setDoc(doc(db, 'rewardContent', 'village_shoutout'), {
        type: 'village_shoutout', message, active, audioURL: '', updatedAt: serverTimestamp()
      });
      setExistingAudioURL('');
      showMsg('Audio removed');
    } catch (e) {
      showMsg('Error removing audio', 'error');
    }
  };

  const previewMessage = message
    .replace('[Name]', 'Rohan')
    .replace('[Village]', 'Wadgaon')
    .replace('[Points]', '47');

  return (
    <div className="admin-card">
      {showPreview && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(160deg, #0f4c2a 0%, #1a7a42 50%, #0a2e18 100%)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '24px'
        }}>
          <div style={{ maxWidth: '320px', width: '100%', textAlign: 'center' }}>
            <div style={{ color: 'white', fontSize: 'clamp(20px,5vw,30px)',
              fontWeight: 800, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {previewMessage}
            </div>
          </div>
          {existingAudioURL && (
            <audio autoPlay src={existingAudioURL} style={{ display: 'none' }} />
          )}
          <button style={{ marginTop: '40px', background: '#007fff', color: 'white',
            border: 'none', padding: '12px 32px', borderRadius: '12px',
            fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => setShowPreview(false)}>
            Close Preview
          </button>
        </div>
      )}
      <h3>Village Shoutout</h3>
      <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
        Shown when a student completes their daily training.
      </p>
      <div style={{ background: '#f0f8ff', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>
        <strong>Placeholders:</strong> <code>[Name]</code> &nbsp;·&nbsp; <code>[Village]</code> &nbsp;·&nbsp; <code>[Points]</code>
      </div>
      {loading ? <p>लोड होत आहे...</p> : (
        <>
          <div className="admin-form-group" style={{ marginBottom: '12px' }}>
            <label>Message (Marathi)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              style={{ minHeight: '80px', resize: 'vertical', whiteSpace: 'pre-wrap' }}
              placeholder="[Name], आज [Village] ने [Points] गुण मिळवले!" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#555' }}>
              Audio {existingAudioURL ? '✓ Uploaded' : '(none — plays silently)'}
            </div>
            {existingAudioURL && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px',
                background: '#e8f5e9', padding: '8px 12px', borderRadius: '8px' }}>
                <audio controls src={existingAudioURL} style={{ height: '32px', flex: 1 }} />
                <button className="admin-btn admin-btn-danger"
                  style={{ padding: '4px 10px', fontSize: '12px' }}
                  onClick={handleAudioRemove}>Remove</button>
              </div>
            )}
            <input type="file" accept="audio/*" style={{ fontSize: '13px' }}
              onChange={e => setAudioFile(e.target.files[0])} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '16px' }}>
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
            Active
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="admin-btn admin-btn-primary" onClick={handleSave}
              disabled={saving || audioUploading}>
              {saving || audioUploading ? 'Saving...' : 'Save'}
            </button>
            <button className="admin-btn admin-btn-secondary" onClick={() => setShowPreview(true)}>
              Preview
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ===== STREAK CELEBRATION =====
function StreakCelebrationTab({ showMsg }) {
  const [message, setMessage] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [existingAudioURL, setExistingAudioURL] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'rewardContent', 'streak_celebration'));
      if (snap.exists()) {
        setMessage(snap.data().message || '');
        setActive(snap.data().active !== false);
        setExistingAudioURL(snap.data().audioURL || '');
      } else {
        setMessage('[Name], तू [Streak] दिवस सलग आलास! तू आग आहेस 🔥');
        setActive(true);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!message.trim()) { showMsg('Message cannot be empty', 'error'); return; }
    if (!window.confirm('Save streak celebration?')) return;
    setSaving(true);
    try {
      let audioURL = existingAudioURL;
      if (audioFile) {
        setAudioUploading(true);
        const audRef = ref(storage, `content/reward-audio/streak_celebration_${Date.now()}`);
        await uploadBytes(audRef, audioFile);
        audioURL = await getDownloadURL(audRef);
        setAudioUploading(false);
      }
      await setDoc(doc(db, 'rewardContent', 'streak_celebration'), {
        type: 'streak_celebration', message: message,
        active, audioURL, updatedAt: serverTimestamp()
      });
      showMsg('Streak celebration saved');
      setAudioFile(null);
      load();
    } catch (e) {
      showMsg('Error saving', 'error');
    } finally {
      setSaving(false);
      setAudioUploading(false);
    }
  };

  const handleAudioRemove = async () => {
    if (!window.confirm('Remove audio?')) return;
    try {
      await setDoc(doc(db, 'rewardContent', 'streak_celebration'), {
        type: 'streak_celebration', message, active, audioURL: '', updatedAt: serverTimestamp()
      });
      setExistingAudioURL('');
      showMsg('Audio removed');
    } catch (e) {
      showMsg('Error removing audio', 'error');
    }
  };

  const previewMessage = message
    .replace('[Name]', 'Rohan')
    .replace('[Streak]', '12');

  return (
    <div className="admin-card">
      {showPreview && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(160deg, #7c2d00 0%, #c2410c 50%, #431407 100%)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '24px'
        }}>
          <div style={{ maxWidth: '320px', width: '100%', textAlign: 'center' }}>
            <div style={{ color: 'white', fontSize: 'clamp(20px,5vw,30px)',
              fontWeight: 800, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {previewMessage}
            </div>
          </div>
          {existingAudioURL && (
            <audio autoPlay src={existingAudioURL} style={{ display: 'none' }} />
          )}
          <button style={{ marginTop: '40px', background: '#007fff', color: 'white',
            border: 'none', padding: '12px 32px', borderRadius: '12px',
            fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => setShowPreview(false)}>
            Close Preview
          </button>
        </div>
      )}
      <h3>Streak Celebration</h3>
      <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
        Shown to celebrate the student's current streak.
      </p>
      <div style={{ background: '#f0f8ff', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>
        <strong>Placeholders:</strong> <code>[Name]</code> &nbsp;·&nbsp; <code>[Streak]</code>
      </div>
      {loading ? <p>लोड होत आहे...</p> : (
        <>
          <div className="admin-form-group" style={{ marginBottom: '12px' }}>
            <label>Message (Marathi)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              style={{ minHeight: '80px', resize: 'vertical', whiteSpace: 'pre-wrap' }}
              placeholder="[Name], तू [Streak] दिवस सलग आलास! 🔥" />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#555' }}>
              Audio {existingAudioURL ? '✓ Uploaded' : '(none — plays silently)'}
            </div>
            {existingAudioURL && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px',
                background: '#e8f5e9', padding: '8px 12px', borderRadius: '8px' }}>
                <audio controls src={existingAudioURL} style={{ height: '32px', flex: 1 }} />
                <button className="admin-btn admin-btn-danger"
                  style={{ padding: '4px 10px', fontSize: '12px' }}
                  onClick={handleAudioRemove}>Remove</button>
              </div>
            )}
            <input type="file" accept="audio/*" style={{ fontSize: '13px' }}
              onChange={e => setAudioFile(e.target.files[0])} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '16px' }}>
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
            Active
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="admin-btn admin-btn-primary" onClick={handleSave}
              disabled={saving || audioUploading}>
              {saving || audioUploading ? 'Saving...' : 'Save'}
            </button>
            <button className="admin-btn admin-btn-secondary" onClick={() => setShowPreview(true)}>
              Preview
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ===== MEDIA REWARDS =====
function MediaRewardsTab({ showMsg }) {
  const [memes, setMemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ dialogueText: '', active: true });
  const [imageFile, setImageFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [previewMeme, setPreviewMeme] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'rewardContent'));
      setMemes(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(d => d.type === 'celebrity_meme'));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { showMsg(err, 'error'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { showMsg(err, 'error'); return; }
    setAudioFile(file);
    setAudioPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!editingId && !imageFile) { showMsg('Image or GIF required', 'error'); return; }
    if (!window.confirm(editingId ? 'Save changes?' : 'Add this media reward?')) return;
    setSaving(true);
    try {
      let imageURL = editingId ? memes.find(m => m.id === editingId)?.imageURL : null;
      let audioURL = editingId ? memes.find(m => m.id === editingId)?.audioURL : null;
      if (imageFile) {
        const imgRef = ref(storage, `content/celebrity-memes/${Date.now()}_image`);
        await uploadBytes(imgRef, imageFile);
        imageURL = await getDownloadURL(imgRef);
      }
      if (audioFile) {
        const audRef = ref(storage, `content/celebrity-memes/${Date.now()}_audio`);
        await uploadBytes(audRef, audioFile);
        audioURL = await getDownloadURL(audRef);
      }
      const docId = editingId || `meme_${Date.now()}`;
      await setDoc(doc(db, 'rewardContent', docId), {
        type: 'celebrity_meme',
        dialogueText: form.dialogueText,
        imageURL, audioURL,
        active: form.active,
        updatedAt: serverTimestamp()
      });
      showMsg(editingId ? 'Updated' : 'Media reward added');
      setForm({ dialogueText: '', active: true });
      setImageFile(null); setAudioFile(null);
      setImagePreview(null); setAudioPreview(null);
      setEditingId(null);
      load();
    } catch (e) {
      showMsg('Error saving: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (meme) => {
    if (!window.confirm('Delete this media reward permanently?')) return;
    try {
      await deleteDoc(doc(db, 'rewardContent', meme.id));
      showMsg('Deleted');
      load();
    } catch (e) {
      showMsg('Error deleting', 'error');
    }
  };

  const handleToggleActive = async (meme) => {
    if (!window.confirm(`Mark as ${meme.active ? 'inactive' : 'active'}?`)) return;
    try {
      await updateDoc(doc(db, 'rewardContent', meme.id), { active: !meme.active });
      load();
    } catch (e) {
      showMsg('Error updating', 'error');
    }
  };

  return (
    <div>
      {previewMeme && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)', zIndex: 2000,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ position: 'relative', maxWidth: '340px', width: '100%' }}>
            <img src={previewMeme.imageURL} alt="preview"
              style={{ width: '100%', borderRadius: '16px', display: 'block' }} />
            {previewMeme.dialogueText && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
                borderRadius: '0 0 16px 16px', padding: '20px 16px 16px' }}>
                <div style={{ color: '#fff', fontSize: 'clamp(16px,4vw,22px)',
                  fontWeight: 800, textAlign: 'center' }}>
                  {previewMeme.dialogueText}
                </div>
              </div>
            )}
          </div>
          {previewMeme.audioURL && (
            <audio autoPlay src={previewMeme.audioURL}
              style={{ marginTop: '16px', width: '100%', maxWidth: '340px' }} />
          )}
          <button style={{ marginTop: '20px', background: '#007fff', color: 'white',
            border: 'none', padding: '12px 32px', borderRadius: '12px',
            fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}
            onClick={() => setPreviewMeme(null)}>
            Close Preview
          </button>
        </div>
      )}
      <div className="admin-card">
        <h3>{editingId ? 'Edit Media Reward' : 'Add New Media Reward'}</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          Upload a photo or GIF with optional audio and optional text overlay.
        </p>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Image or GIF (max 2MB) *</label>
            <input type="file" accept="image/*" onChange={handleImageChange} />
            {imagePreview && (
              <img src={imagePreview} alt="preview"
                style={{ width: '120px', height: '120px', objectFit: 'cover',
                  borderRadius: '8px', marginTop: '8px' }} />
            )}
          </div>
          <div className="admin-form-group">
            <label>Audio File (optional, max 2MB)</label>
            <input type="file" accept="audio/*" onChange={handleAudioChange} />
            {audioPreview && <audio controls src={audioPreview} style={{ marginTop: '8px', width: '100%' }} />}
          </div>
        </div>
        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Text overlay (optional)</label>
          <input type="text" value={form.dialogueText}
            onChange={e => setForm({ ...form, dialogueText: e.target.value })}
            placeholder="e.g. एकदम झक्कास! 💥" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '12px' }}>
          <input type="checkbox" checked={form.active}
            onChange={e => setForm({ ...form, active: e.target.checked })} />
          Active
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Uploading...' : editingId ? 'Save Changes' : 'Add Media Reward'}
          </button>
          {editingId && (
            <button className="admin-btn admin-btn-secondary"
              onClick={() => { setEditingId(null); setForm({ dialogueText: '', active: true });
                setImageFile(null); setAudioFile(null); setImagePreview(null); setAudioPreview(null); }}>
              Cancel
            </button>
          )}
        </div>
      </div>
      <div className="admin-card">
        <h3>All Media Rewards ({memes.length})</h3>
        {loading ? <p>लोड होत आहे...</p> : memes.map(meme => (
          <div key={meme.id} style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
            marginBottom: '8px', background: meme.active ? '#f8f9fa' : '#fef2f2',
            borderRadius: '8px', border: '1px solid #e9ecef', flexWrap: 'wrap' }}>
            {meme.imageURL && (
              <img src={meme.imageURL} alt="reward"
                style={{ width: '60px', height: '60px', objectFit: 'cover',
                  borderRadius: '8px', flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: '120px' }}>
              <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                {meme.dialogueText || '(no text)'}
              </div>
              {meme.audioURL && <audio controls src={meme.audioURL} style={{ height: '32px', width: '200px' }} />}
            </div>
            <span style={{ fontSize: '12px', color: meme.active ? '#28a745' : '#dc3545',
              fontWeight: 600, minWidth: '55px' }}>
              {meme.active ? 'Active' : 'Inactive'}
            </span>
            <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => setPreviewMeme(meme)}>Preview</button>
            <button className="admin-btn admin-btn-secondary" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => { setEditingId(meme.id);
                setForm({ dialogueText: meme.dialogueText || '', active: meme.active });
                setImagePreview(meme.imageURL); setAudioPreview(meme.audioURL); }}>
              Edit
            </button>
            <button className="admin-btn" style={{ padding: '4px 10px', fontSize: '13px',
              background: meme.active ? '#fff3cd' : '#d4edda', border: 'none',
              borderRadius: '8px', cursor: 'pointer' }}
              onClick={() => handleToggleActive(meme)}>
              {meme.active ? 'Deactivate' : 'Activate'}
            </button>
            <button className="admin-btn admin-btn-danger" style={{ padding: '4px 10px', fontSize: '13px' }}
              onClick={() => handleDelete(meme)}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}